import { inject, Injectable } from '@angular/core';
import { MaintenanceResponse } from '../models/maintenance.model';
import { BehaviorSubject, interval, Observable, of, Subscription, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceService {

  private http = inject(HttpClient);
  private router = inject(Router);
  private maintenanceModeSubject = new BehaviorSubject<boolean>(false);
  public maintenanceMode$ = this.maintenanceModeSubject.asObservable();

  private maintenanceData: MaintenanceResponse | null = null;
  private readonly apiUrl = environment.api.baseUrl;

  // Timer properties
  private countdownSubscription?: Subscription;
  private remainingSecondsSubject = new BehaviorSubject<number>(0);
  public remainingSeconds$ = this.remainingSecondsSubject.asObservable();
  
  private isCountdownActiveSubject = new BehaviorSubject<boolean>(false);
  public isCountdownActive$ = this.isCountdownActiveSubject.asObservable();

   // ========================================
  private backdoorEnabled = false;
  private readonly BACKDOOR_PARAM = 'from';
  private readonly BACKDOOR_VALUE = 'testingeclaimsmaintainencewindow'; 
 
  isBackdoorEnabled(): boolean {
    return this.backdoorEnabled;
  }

  enableBackdoor(): void {
    this.backdoorEnabled = true;
  }

  disableBackdoor(): void {
    this.backdoorEnabled = false;
  }

  checkBackdoorParam(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const bypassParam = urlParams.get(this.BACKDOOR_PARAM);
    
    if (bypassParam === this.BACKDOOR_VALUE) {
      this.enableBackdoor();
      // Remove the parameter from URL to hide it
      this.removeBackdoorParamFromUrl();
      return true;
    }
    
    // Check if backdoor was previously enabled in this session
    if (this.isBackdoorEnabled()) {
      this.backdoorEnabled = true;
      return true;
    }
    
    return false;
  }

  private removeBackdoorParamFromUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(this.BACKDOOR_PARAM);
    window.history.replaceState({}, document.title, url.toString());
  }


  /**
   * Check maintenance status from API
   */
  checkMaintenanceStatus(): Observable<MaintenanceResponse> {
    const url = this.apiUrl + 'bff/maintenance/status';

    if(this.isBackdoorEnabled()){
      return of({
        maintenanceMode: false,
        timeToMaintenanceInSeconds: 0
      })
    }

    return this.http.get<MaintenanceResponse>(url).pipe(
      tap(response => {
        this.maintenanceData = response;
        this.maintenanceModeSubject.next(response.maintenanceMode && response.timeToMaintenanceInSeconds === 0);

        // Handle maintenance countdown
        if (response.timeToMaintenanceInSeconds !== undefined && 
            response.timeToMaintenanceInSeconds > 0 && 
            response.timeToMaintenanceInSeconds <= 3600) {
          this.startCountdown(response.timeToMaintenanceInSeconds);
        } else {
          this.stopCountdown();
        }
        
        // If already in maintenance mode, redirect immediately
        if (this.isMaintenanceMode()) {
          this.redirectToMaintenance();
        }
      })
    );
  }

  private startCountdown(seconds: number): void {
    if (this.backdoorEnabled) {
      return;
    }
    // Stop any existing countdown
    this.stopCountdown();
    this.remainingSecondsSubject.next(seconds);
    this.isCountdownActiveSubject.next(true);
    
    // console.log(`Maintenance countdown started: ${seconds} seconds (${this.formatTime(seconds)})`);
    
    // Create countdown timer (tick every second)
    this.countdownSubscription = interval(1000).subscribe(() => {
      const currentSeconds = this.remainingSecondsSubject.value;
      
      if (currentSeconds <= 0) {
        // Countdown reached zero - redirect to maintenance
        this.stopCountdown();
        this.redirectToMaintenance();
      } else {
        // Decrement counter
        this.remainingSecondsSubject.next(currentSeconds - 1);
      }
    });
  }

  private stopCountdown(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }
    this.isCountdownActiveSubject.next(false);
    this.remainingSecondsSubject.next(0);
  }

  private redirectToMaintenance(): void {
    if (this.backdoorEnabled) {
      return;
    }
    this.maintenanceModeSubject.next(true);
    this.router.navigate(['/maintenance']);
  }


  /**
   * Get current maintenance status
   */
  isMaintenanceMode(): boolean {
    if (this.backdoorEnabled) {
      return false;
    }
    return this.maintenanceModeSubject.value;
  }

  /**
   * Get maintenance data
   */
  getMaintenanceData(): MaintenanceResponse | null {
    return this.maintenanceData;
  }

  /**
   * Set maintenance mode manually (for testing)
   */
  setMaintenanceMode(isEnabled: boolean): void {
    if (this.backdoorEnabled) {
      return;
    }
    this.maintenanceModeSubject.next(isEnabled);
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }
}
